import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Target, Eye, Award, Users, Shield, CheckCircle, Lightbulb, HeartHandshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const values = [
  {
    icon: Lightbulb,
    title: 'Innovation',
    description: 'Continuously embracing new technologies and best practices to deliver modern cloud solutions.'
  },
  {
    icon: Shield,
    title: 'Reliability',
    description: 'Providing stable, secure, and dependable services that businesses can trust.'
  },
  {
    icon: Users,
    title: 'Customer Success',
    description: 'Focusing on customer needs and creating measurable business value.'
  },
  {
    icon: Award,
    title: 'Excellence',
    description: 'Striving for high standards in service quality, technical expertise, and operational performance.'
  },
  {
    icon: HeartHandshake,
    title: 'Integrity',
    description: 'Acting with honesty, transparency, and professionalism in everything we do.'
  }];


  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#020B1A] via-[#061426] to-[#0A1E3F] text-white pt-32 pb-24 mt-16 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-96 h-96 bg-cyan-500/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-600/30 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              About Axora Cloud Service
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Empowering businesses through innovative AWS cloud solutions and digital transformation
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-gradient-to-b from-[#061426] to-[#020B1A] relative overflow-hidden">
        {/* Technology Background Pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              radial-gradient(circle at 20% 50%, rgba(255, 149, 0, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 20%, rgba(34, 211, 238, 0.1) 0%, transparent 50%)
            `
          }}></div>
          
          {/* Geometric Grid Pattern */}
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                <circle cx="25" cy="25" r="1" fill="currentColor" opacity="0.3" />
                <line x1="0" y1="25" x2="50" y2="25" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
                <line x1="25" y1="0" x2="25" y2="50" stroke="currentColor" strokeWidth="0.5" opacity="0.2" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
          
          {/* Cloud Network Nodes */}
          <div className="absolute top-10 left-20 w-32 h-32 rounded-full border-2 border-orange-500 opacity-20"></div>
          <div className="absolute top-40 right-32 w-24 h-24 rounded-full border-2 border-blue-500 opacity-15"></div>
          <div className="absolute bottom-32 left-1/3 w-28 h-28 rounded-full border-2 border-cyan-500 opacity-15"></div>
          <div className="absolute bottom-20 right-1/4 w-20 h-20 rounded-full border-2 border-orange-400 opacity-20"></div>
          
          {/* Connection Lines */}
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <line x1="10%" y1="15%" x2="85%" y2="45%" stroke="rgb(59, 130, 246)" strokeWidth="1" opacity="0.1" strokeDasharray="5,5" />
            <line x1="35%" y1="75%" x2="75%" y2="25%" stroke="rgb(249, 115, 22)" strokeWidth="1" opacity="0.1" strokeDasharray="5,5" />
            <line x1="20%" y1="50%" x2="70%" y2="80%" stroke="rgb(34, 211, 238)" strokeWidth="1" opacity="0.1" strokeDasharray="5,5" />
          </svg>
          
          {/* Floating Hexagons */}
          <div className="absolute top-1/4 right-1/4">
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg" opacity="0.15">
              <path d="M30 5 L50 17.5 L50 42.5 L30 55 L10 42.5 L10 17.5 Z" stroke="rgb(249, 115, 22)" strokeWidth="1.5" fill="none" />
            </svg>
          </div>
          <div className="absolute bottom-1/3 left-1/4">
            <svg width="45" height="45" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg" opacity="0.12">
              <path d="M30 5 L50 17.5 L50 42.5 L30 55 L10 42.5 L10 17.5 Z" stroke="rgb(59, 130, 246)" strokeWidth="1.5" fill="none" />
            </svg>
          </div>
          
          {/* Binary Code Effect */}
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-1/4 left-10 text-xs font-mono text-gray-400 opacity-10">
              01001000 01010100 01010100
            </div>
            <div className="absolute top-2/3 right-20 text-xs font-mono text-gray-400 opacity-10">
              01010000 01010011 00100000
            </div>
            <div className="absolute bottom-1/4 left-1/2 text-xs font-mono text-gray-400 opacity-10">
              01000001 01010111 01010011
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-8 text-center">Our Story</h2>
            <div className="prose prose-lg max-w-none text-gray-300 leading-relaxed space-y-6">
              <p className="text-lg">
                <strong>Axora Cloud Service</strong> was established in 2026 as a technology-focused cloud services company preparing its commercial cloud engineering and AWS-based solution capabilities.
              </p>
              <p className="text-lg">
                As a subsidiary of <a href="https://unitedoffice.net/" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 font-semibold underline transition-colors">United Office Group</a>, Axora Cloud Service leverages industry expertise and regional resources to help businesses accelerate their digital transformation journey. We plan to provide a wide range of AWS-related services, including cloud migration, infrastructure management, cloud security, disaster recovery, system monitoring, and managed support services.
              </p>
              <p className="text-lg">
                Our goal is to empower organizations with secure, scalable, and cost-effective cloud solutions that drive innovation, operational efficiency, and sustainable business growth.
              </p>
              <p className="text-lg">
                Looking ahead, Axora Cloud Service aims to become a trusted cloud technology partner globally, helping enterprises adopt modern cloud technologies and unlock new opportunities in the digital economy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 bg-gradient-to-b from-[#020B1A] to-[#061426]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Vision */}
            <div className="bg-gradient-to-br from-[#061426] to-[#0A1E3F] rounded-2xl p-10 shadow-lg border-2 border-cyan-500/30 hover:border-cyan-400 transition-all duration-300" style={{ boxShadow: '0 4px 30px rgba(0, 212, 255, 0.1)' }}>
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-6" style={{ boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)' }}>
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Vision</h3>
              <p className="text-lg text-gray-300 leading-relaxed">
                To shape the future of digital transformation by becoming a world-leading provider of cloud technology solutions and services.
              </p>
            </div>

            {/* Mission */}
            <div className="bg-gradient-to-br from-[#061426] to-[#0A1E3F] rounded-2xl p-10 shadow-lg border-2 border-cyan-500/30 hover:border-cyan-400 transition-all duration-300" style={{ boxShadow: '0 4px 30px rgba(0, 212, 255, 0.1)' }}>
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-6" style={{ boxShadow: '0 0 20px rgba(0, 212, 255, 0.4)' }}>
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Mission</h3>
              <p className="text-lg text-gray-300 leading-relaxed">
                To help organizations harness the full potential of cloud technology through expertise, innovation, and customer-centric services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gradient-to-b from-[#061426] to-[#020B1A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-[#061426] to-[#0A1E3F] border-2 border-cyan-500/30 rounded-xl p-8 transition-all duration-300 hover:border-cyan-400 hover:shadow-2xl hover:-translate-y-1"
                  style={{ boxShadow: '0 4px 20px rgba(0, 212, 255, 0.1)' }}>

                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-cyan-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#020B1A] via-[#061426] to-[#0A1E3F] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-20 w-64 h-64 bg-cyan-500/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-64 h-64 bg-blue-600/30 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">Accelerate your digital transformation with Axora Cloud Service.

          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-medium px-10 py-6 text-lg transition-all duration-300" style={{ boxShadow: '0 0 30px rgba(0, 212, 255, 0.5)' }}>
              Get Started Today
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>);

};

export default AboutPage;